import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { api as authApi } from "../lib/api";
import {
  AlertTriangle, CheckCircle2, Clock, XCircle, HelpCircle, Package, Layers,
  Phone, ExternalLink, Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_URL = "https://unionrsm.com/pages/contatti";

const STATUS_META = {
  valido: { label: "VALIDO", style: { backgroundColor: "#10b981", color: "#fff" }, icon: CheckCircle2 },
  in_scadenza: { label: "IN SCADENZA", style: { backgroundColor: "#ffcc00", color: "#000" }, icon: Clock },
  scaduto: { label: "SCADUTO", style: { backgroundColor: "#ff3b30", color: "#fff" }, icon: AlertTriangle },
  fallito: { label: "NON IDONEO", style: { backgroundColor: "#ff3b30", color: "#fff" }, icon: XCircle },
  non_ispezionato: { label: "NON ISPEZIONATO", style: { backgroundColor: "#a3a3a3", color: "#fff" }, icon: HelpCircle },
  non_revisionato: { label: "DA VERIFICARE", style: { backgroundColor: "#a3a3a3", color: "#fff" }, icon: HelpCircle },
};

const NOTE1_LABELS = {
  tabella_portata_errata: "Tabella portata errata",
  tabella_portata_mancante: "Tabella portata mancante",
  deformazione: "Deformazione",
  modifica_non_conforme: "Modifica non conforme",
  torsione: "Torsione",
  fissaggio_mancante: "Fissaggio mancante",
  pezzo_mancante: "Pezzo mancante",
};

function fmt(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime()) || dt.getFullYear() < 1900 || dt.getFullYear() > 2200) return "—";
    return dt.toLocaleDateString("it-IT");
  } catch { return "—"; }
}

function daysUntil(d) {
  if (!d) return null;
  try {
    const now = new Date(); now.setHours(0,0,0,0);
    const then = new Date(d); then.setHours(0,0,0,0);
    return Math.round((then.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function ContactBanner({ status, days }) {
  const urgent = status === "scaduto" || status === "fallito" || (days != null && days <= 30);
  if (!urgent) return null;
  const title = status === "scaduto"
    ? "Elemento SCADUTO"
    : status === "fallito"
    ? "Elemento NON IDONEO"
    : `Scadenza imminente (${days} gg)`;
  return (
    <div className="bru-card p-5 animate-pulse-slow text-white" style={{ backgroundColor: "#ff3b30" }} data-testid="public-check-contact-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-7 h-7 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">Azione richiesta</div>
          <div className="text-xl font-bold">{title}</div>
          <div className="text-sm opacity-90 mt-1">
            Contatta il tuo responsabile o il centro Union per programmare l'intervento.
          </div>
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 bg-white text-[#ff3b30] font-bold text-sm uppercase tracking-wider px-4 py-2 rounded-md hover:bg-neutral-100"
            data-testid="public-check-contact-button"
          >
            <Phone className="w-4 h-4" />
            Contattaci ora
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

function DamagePhoto({ p }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null, revoked = false;
    (async () => {
      try {
        const res = await fetch(`${API}/public/files/${p.photo}`);
        if (!res.ok) return;
        const b = await res.blob();
        u = URL.createObjectURL(b);
        if (!revoked) setUrl(u);
      } catch { /* ignore */ }
    })();
    return () => { revoked = true; if (u) { try { URL.revokeObjectURL(u); } catch { /* */ } } };
  }, [p.photo]);
  const caption = [p.posto, p.riferimento_scaffalatura].filter(Boolean).join(" · ");
  const note = p.nota1 ? NOTE1_LABELS[p.nota1] : p.nota2 || "";
  return (
    <a
      href={`${API}/public/files/${p.photo}`}
      target="_blank"
      rel="noopener noreferrer"
      className="bru-card overflow-hidden group block"
      data-testid={`public-damage-${p.photo}`}
    >
      <div className="aspect-square bg-neutral-100">
        {url ? (
          <img src={url} alt={caption} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-neutral-400" />
          </div>
        )}
      </div>
      <div className="px-3 py-2 border-t-[1.5px] border-black text-[11px]">
        <div className="font-bold truncate">#{p.row_number} {caption}</div>
        {note && <div className="text-[#ff5a00] truncate">{note}</div>}
      </div>
    </a>
  );
}

export default function PublicCheck() {
  const { kind, id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // 1) If the user is already logged in as admin, redirect to the internal
      //    detail page (quick-inspection flow from QR scan).
      try {
        const me = await authApi.get("/auth/me");
        if (me?.data?.role === "admin") {
          const path = kind === "dpi" ? `/devices/${id}` : `/scaffolds/${id}`;
          setRedirectTo(path);
          setLoading(false);
          return;
        }
      } catch {
        /* not logged in: fall through to public check */
      }
      try {
        const endpoint = kind === "dpi" ? "dpi" : "scaffold";
        const res = await axios.get(`${API}/public/check/${endpoint}/${id}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Elemento non trovato");
      }
      setLoading(false);
    })();
  }, [kind, id]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  const meta = (data && STATUS_META[data.status]) || STATUS_META.non_ispezionato;
  const Icon = meta.icon;
  const isDpi = kind === "dpi";
  const days = data ? daysUntil(data.expiration_date) : null;

  return (
    <div className="min-h-screen bg-neutral-100 flex flex-col" data-testid="public-check-page">
      <header className="bg-black text-white px-4 sm:px-8 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <a href="/login" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="public-check-logo-link">
            <img src="/logo-union.png" alt="Union" className="h-10 w-auto object-contain rounded-lg" />
            <div>
              <div className="font-bold text-xl" >UNION CHECK</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff5a00]">SICURI SEMPER</div>
            </div>
          </a>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 max-w-2xl w-full mx-auto">
        {loading ? (
          <div className="bru-card p-10 text-center">
            <div className="tag-label">CARICAMENTO…</div>
          </div>
        ) : error ? (
          <div className="bru-card p-10 text-center" data-testid="public-check-error">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-[#ff3b30]" />
            <div className="tag-label mb-2">ERRORE</div>
            <h2 className="text-2xl font-bold" >{error}</h2>
            <p className="text-sm text-neutral-600 mt-2">Verifica il QR code o contatta il responsabile sicurezza.</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="bru-card p-6" style={meta.style} data-testid="public-check-status">
              <div className="flex items-center gap-4">
                <Icon className="w-12 h-12 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Stato</div>
                  <div className="text-3xl font-bold truncate" >
                    {meta.label}
                  </div>
                </div>
              </div>
              {data.expiration_date && (
                <div className="mt-4 pt-4 border-t border-current/20">
                  <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Prossima scadenza</div>
                  <div className="text-xl font-bold">
                    {fmt(data.expiration_date)}
                    {days != null && (
                      <span className="text-sm font-bold opacity-80 ml-2">
                        ({days < 0 ? `scaduto da ${Math.abs(days)} gg` : `${days} gg rimanenti`})
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <ContactBanner status={data.status} days={days} />

            <div className="bru-card p-6" data-testid="public-check-info">
              <div className="flex items-center gap-2 mb-4">
                {isDpi ? <Package className="w-5 h-5" /> : <Layers className="w-5 h-5" />}
                <div className="tag-label">{isDpi ? "DPI" : "Scaffalatura"}</div>
              </div>
              <h1 className="text-3xl font-bold mb-1 tracking-tight" >
                {isDpi ? data.code : data.reference}
              </h1>
              {isDpi && data.category_label && <div className="text-sm text-neutral-600 mb-4">{data.category_label}</div>}
              {!isDpi && data.address && <div className="text-sm text-neutral-600 mb-4">{data.address}</div>}

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-sm">
                {data.client_name && (
                  <div><dt className="tag-label">Cliente</dt><dd className="font-bold">{data.client_name}</dd></div>
                )}
                {isDpi && data.dipendente && (
                  <div><dt className="tag-label">Dipendente</dt><dd className="font-bold">{data.dipendente}</dd></div>
                )}
                {isDpi && data.brand && (
                  <div><dt className="tag-label">Marca</dt><dd className="font-bold">{data.brand}</dd></div>
                )}
                {isDpi && data.model && (
                  <div><dt className="tag-label">Modello</dt><dd className="font-bold">{data.model}</dd></div>
                )}
                {isDpi && data.serial_number && (
                  <div><dt className="tag-label">Matricola</dt><dd className="font-bold font-mono">{data.serial_number}</dd></div>
                )}
                {isDpi && data.last_inspection_date && (
                  <div><dt className="tag-label">Ultima ispezione</dt><dd className="font-bold">{fmt(data.last_inspection_date)}</dd></div>
                )}
                {!isDpi && data.last_revision_date && (
                  <div><dt className="tag-label">Ultima verifica</dt><dd className="font-bold">{fmt(data.last_revision_date)}</dd></div>
                )}
                {isDpi && data.production_date && (
                  <div><dt className="tag-label">Data produzione</dt><dd className="font-bold">{fmt(data.production_date)}</dd></div>
                )}
                {isDpi && data.first_use_date && (
                  <div><dt className="tag-label">Primo utilizzo</dt><dd className="font-bold">{fmt(data.first_use_date)}</dd></div>
                )}
                {isDpi && data.useful_life_years != null && (
                  <div><dt className="tag-label">Vita utile</dt><dd className="font-bold">{data.useful_life_years} anni</dd></div>
                )}
                {isDpi && data.life_expiration_date && (
                  <div><dt className="tag-label">Fine vita utile</dt><dd className="font-bold">{fmt(data.life_expiration_date)}</dd></div>
                )}
                {!isDpi && data.damage_count > 0 && (
                  <div><dt className="tag-label">Righe rilevate</dt><dd className="font-bold">{data.damage_count}</dd></div>
                )}
              </dl>
            </div>

            {/* Revision outcome */}
            {(isDpi ? data.last_result || data.last_notes : data.last_criticita || data.last_conclusioni || data.last_notes) && (
              <div className="bru-card p-6" data-testid="public-check-outcome">
                <div className="tag-label mb-3">Esito {isDpi ? "Ispezione" : "Verifica"}</div>
                {isDpi && data.last_result && (
                  <div className="mb-3">
                    <span
                      className="bru-badge text-white"
                      style={{ backgroundColor: data.last_result === "superato" ? "#10b981" : "#ff3b30" }}
                    >
                      {data.last_result === "superato" ? "Superata" : "Non superata"}
                    </span>
                  </div>
                )}
                {data.last_notes && (
                  <div className="mb-3"><div className="tag-label mb-1">Note</div><div className="text-sm whitespace-pre-line">{data.last_notes}</div></div>
                )}
                {!isDpi && data.last_criticita && (
                  <div className="mb-3"><div className="tag-label mb-1">Criticità</div><div className="text-sm whitespace-pre-line">{data.last_criticita}</div></div>
                )}
                {!isDpi && data.last_conclusioni && (
                  <div><div className="tag-label mb-1">Conclusioni</div><div className="text-sm whitespace-pre-line">{data.last_conclusioni}</div></div>
                )}
              </div>
            )}

            {/* Damage photos for scaffolds */}
            {!isDpi && (data.damage_photos || []).length > 0 && (
              <div className="bru-card p-6" data-testid="public-check-damages">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4" />
                  <div className="tag-label">Foto danni rilevati ({data.damage_photos.length})</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {data.damage_photos.map((p) => (
                    <DamagePhoto key={p.photo} p={p} />
                  ))}
                </div>
              </div>
            )}

            <div className="text-center text-xs text-neutral-500 pt-4">
              Verifica pubblica · <a href="/login" className="underline hover:text-[#ff5a00]">Accedi al sistema</a>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-[11px] text-neutral-500 py-4 px-4">
        Gruppo Union · Via Lorenzo Tabellione, 13 · info@union.sm · 0549 942 493
      </footer>
    </div>
  );
}
